import { Component, OnInit, OnDestroy, HostListener, ViewEncapsulation, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, Subscription, filter } from 'rxjs';
// import { filter } from 'rxjs/operators';

import { Utils } from '@services/utils.service';
import { MenuSections } from '@app/interfaces/menu.interface';
import { Service as LayoutService } from '@services/layout.service';
import { Service as AlertService } from '@app/services/alert.service';
import { Service as ServiceStorage } from '@services/storage.service';
import { Service as ServiceSocket } from '@app/services/web_socket.service';
import { Service as UserService } from '@app/services/user.service';
import { Service as NotificationService } from '@app/services/notification.service';
import { Service as TicketSupportCloseService } from '@services/ticket_support_close.service'

@Component({
    standalone: true,
    selector: 'app-navbar',
    templateUrl: './navbar.html',
    styleUrl: './navbar.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, TranslatePipe, RouterModule],
})
export class Navbar implements OnInit, OnDestroy {
    private Translation: any = {};
    public uuid: any;
    public user_data: any = '';

    private socketSubscription!: Subscription;
    private router_subscription!: Subscription;

    public list_data: any[] = [];
    public list_summary: any = { total: 0 };
    public is_dropdown_open: boolean = false;

    public breadcrumb_items: any[] = [];
    public MenuSections = MenuSections;

    public countdown_display: { display: string, item_id: number, remaining_seconds?: number } | null = null;

    constructor(
        private Utils: Utils,
        private Router: Router,
        private ElementRef: ElementRef,
        private TranslateService: TranslateService,
        private LayoutService: LayoutService,
        private AlertService: AlertService,
        private ServiceStorage: ServiceStorage,
        private ServiceSocket: ServiceSocket,
        private UserService: UserService,
        private TicketSupportCloseService: TicketSupportCloseService,
        private NotificationService: NotificationService,
    ) { }

    async ngOnInit() {
        this.Translation = await firstValueFrom(this.TranslateService.get(['SUCCESS', 'ERRORS', 'BUTTONS', 'LOGIN', 'COMMON', 'NOTIFICATION', 'BREAD_CRUMBS']));
        this.uuid = await this.ServiceStorage.get('uuid');
        this.user_data = await this.ServiceStorage.get('user_data');

        await this.initializeBreadcrumb();
        await this.TicketSupportCloseService.restoreTimer();

        this.TicketSupportCloseService.countdown$.subscribe(value => { this.countdown_display = value; });

        if (!this.uuid) {
            this.uuid = this.Utils.getUUID();
            await this.ServiceStorage.set('uuid', `${this.uuid}`);
        }

        await this.loadDataNotification();
        this.socketReceive();
    }

    ngOnDestroy(): void {
        if (this.socketSubscription) {
            this.socketSubscription.unsubscribe();
        }

        if (this.router_subscription) {
            this.router_subscription.unsubscribe();
        }
    }

    async loadDataNotification() {
        const return_data = await this.NotificationService.getList({ active: 1, deleted: 1, recursive: true, 'notification.user_id': this.user_data.user_id, 'notification.read[null]': true, 'notification.type': 'local', order: 'DESC', query:true });
        // console.log(return_data);

        if (return_data.success == 1) {
            this.list_data = return_data.data || [];
            this.list_summary = return_data.summary || { total: 0 };
        }
    }

    async initializeBreadcrumb() {
        const is_administrator_role = ['admin', 'super_admin', 'system'].includes(await this.ServiceStorage.get('role'));

        this.router_subscription = this.Router.events.pipe(
            filter(router_event => router_event instanceof NavigationEnd)
        ).subscribe((router_event: any) => {
            this.buildBreadcrumb(router_event.urlAfterRedirects, is_administrator_role);
            this.detectRouteChange(router_event.urlAfterRedirects);
        });

        this.buildBreadcrumb(this.Router.url, is_administrator_role);
        this.detectRouteChange(this.Router.url);
    }

    buildBreadcrumb(current_url: string, is_administrator_role: boolean) {
        this.breadcrumb_items = [];
        const url_segments = current_url.split('?')[0].split('/').filter(url_segment => url_segment != '');

        if (url_segments.length == 0) {
            return;
        }

        const base_view = is_administrator_role ? 'table' : 'list';
        const dashboard_item = this.findDashboardItem(url_segments[0]);
        const menu_item = this.findMenuItem(url_segments[0]);

        (dashboard_item) ? this.breadcrumb_items.push({ title: dashboard_item.title, route: dashboard_item.route, active: false }) : null;
        

        this.breadcrumb_items.push({
            title: menu_item ? menu_item.title : `BREAD_CRUMBS.${url_segments[0].toUpperCase()}`,
            route: menu_item?.route ? menu_item.route.replace(/\/list|\/table/g, `/${base_view}`) : `/${url_segments[0]}/${base_view}`,
            active: ['table', 'list'].includes(url_segments[1] || '')
        });

        if (url_segments[1] && !['table', 'list'].includes(url_segments[1])) {
            this.breadcrumb_items.push({ title: `BREADCRUMB.${url_segments[1].toUpperCase()}`, route: null, active: true });
        }

        (this.breadcrumb_items.length > 0) ? this.breadcrumb_items[this.breadcrumb_items.length - 1].active = true : null;

    }

    findMenuItem(module_name: string): any {
        for (const section of this.MenuSections) {
            if (section.route) {
                const route_segments = section.route.split('/').filter(route_segment => route_segment !== '');

                if (route_segments[0] === module_name && section.breadcrumb_parent === true) {
                    return section;
                }
            }

            if (section.items) {
                for (const item of section.items) {
                    const route_segments = item.route.split('/').filter(route_segment => route_segment !== '');

                    if (route_segments[0] === module_name && item.breadcrumb_parent === true) {
                        return item;
                    }
                }
            }
        }

        return null;
    }

    findDashboardItem(module_name: string): any {
        for (const section of this.MenuSections) {
            if (section.route && section.breadcrumb === true) {
                const route_segments = section.route.split('/').filter(route_segment => route_segment !== '');

                if (route_segments[0] === module_name) {
                    return section;
                }
            }

            if (section.items) {
                for (const item of section.items) {
                    if (item.breadcrumb === true) {
                        const route_segments = item.route.split('/').filter(route_segment => route_segment !== '');

                        if (route_segments[0] === module_name) {
                            return item;
                        }
                    }
                }
            }
        }

        return null;
    }

    async logout() {
        this.ServiceStorage.clear();
        this.Router.navigateByUrl('/');
    }

    toggleSidenav() {
        this.LayoutService.toggleMiniSidenav();
    }

    async changeRead(item_id: number) {
        const return_data = await this.NotificationService.changeRead(item_id);
        // console.log(return_data);

        if (return_data.success == 1) {
            this.decreaseNotification(item_id);
            // this.socketSend('ipanda', 'all', 'update-notification-count', { item_id: item_id });
        }
    }

    async onNotificationClick(item: any) {
        this.closeDropdown();
        // console.log(item);

        if (item.notification_id) {
            this.changeRead(item.notification_id);
        }

        if (item.params && item.params.url) {
            this.Router.navigate([item.params.url]);
        }
    }

    goToTracking() {
        if (!this.countdown_display?.item_id) {
            return;
        }

        this.Router.navigate(['/ticket_support/tracking', this.countdown_display.item_id]);
    }

    trySocket() {
        this.ServiceSocket.send('ipanda', 'all', 'test', { message: 'test from ticket_support list' }, this.uuid);
    }

    socketSend(channel: any, room: any, action: any, data: any) {
        this.ServiceSocket.send(channel, room, action, data);
    }

    socketReceive() {
        this.socketSubscription = this.ServiceSocket.receive().subscribe(async (socket_data: any) => {
            const is_valid_room = (socket_data.room === 'all') ? true : (socket_data.room === this.uuid ? true : false);
            const parsed_data = (typeof socket_data.data === 'string') ? JSON.parse(socket_data.data) : socket_data.data;
            // console.log(parsed_data);

            if (is_valid_room === false) {
                return;
            }

            switch (socket_data.action) {
                case 'test':
                    this.AlertService.toast({ type: 'success', title: 'Socket Test', message: JSON.stringify(socket_data.data) });
                    break;

                case 'create-ticket_support':
                    const params = (typeof parsed_data.params === 'string') ? JSON.parse(parsed_data.params) : parsed_data.params;
                    await this.getLastNotification(parsed_data.item_id ? parsed_data.item_id : null);

                    setTimeout(async () => {
                        this.AlertService.toast({ type: 'success', title: parsed_data?.title, message: parsed_data?.message, url: parsed_data?.params?.url || null, timer: 20000 });
                    }, 800);
                    break;
                
                case 'update-notification-count':
                    let item_id = parsed_data.item_id ? parsed_data.item_id : null;

                    if (item_id !== null) {
                        await this.decreaseNotification(item_id);
                    }
                    break;
            }
        });
    }

    async decreaseNotification(item_id: any) {
        const initial_length = this.list_data.length;
        this.list_data = this.list_data.filter(item => item.item_id !== item_id);
        const items_removed = initial_length - this.list_data.length;

        if (items_removed > 0) {
            this.list_summary.total = this.list_summary.total - 1;
        }
    }

    async getLastNotification(item_id: any) {
        const return_data = await this.NotificationService.getList({ 'notification.notification_id': item_id });
        // console.log(return_data);

        if (return_data.success === 1 && return_data.data.length > 0) {
            const row = return_data.data[0];
            const is_duplicate = this.list_data.some((item: any) => item.notification_id == row.notification_id);

            if (is_duplicate === false) {
                this.list_data.unshift(row);
                this.list_summary.total = this.list_summary.total + 1;
            } else {
                console.log(return_data);
            }
        }
    }

    toggleDropdown(event: Event) {
        event.stopPropagation();
        this.is_dropdown_open = !this.is_dropdown_open;
    }

    closeDropdown() {
        this.is_dropdown_open = false;
    }

    detectRouteChange(current_url: string) {
        const url_without_parameters = current_url.split('?')[0];
        const url_segments = url_without_parameters.split('/').filter(url_segment => url_segment !== '');
        const module_name = url_segments[0] || '';
        const action_name = url_segments[1] || '';
        const item_id = url_segments[2] || null;

        // console.log('url_without_parameters', url_without_parameters);
        // console.log('url_segments', url_segments);
        // console.log('module_name', module_name);
        // console.log('action_name', action_name);
        // console.log('item_id', item_id);

        switch (module_name) {
            case 'ticket_support':
                this.onTicketSupportRoute(url_without_parameters, action_name, item_id);
                break;

            case 'notification':
                this.onNotificationRoute();
                break;

            case 'dashboard':
                this.onDashboardRoute();
                break;
        }
    }

    onTicketSupportRoute(url_without_parameters: string, action_name?: string, item_id?: any) {
        if (action_name == 'view' || action_name == 'update' || action_name == 'tracking') {
            if (this.list_data!.length > 0) {
                for (var i = 0; i < this.list_data!.length; i++) {
                    const row = this.list_data![i];
                    // console.log('row', row);
                    
                    if (row.params != undefined && row.params.url != undefined && row.params.url == url_without_parameters) {
                        // console.log('row', row);
                        // this.list_data!.splice(i, 1);
                        
                        this.changeRead(row.item_id);
                        break;
                    }
                }
            }
        }
    }

    onNotificationRoute() {
        console.log('notification route actions');
    }

    onDashboardRoute() {
        console.log('dashboard route actions');
    }

    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        if (!this.ElementRef.nativeElement.contains(event.target)) {
            this.closeDropdown();
        }
    }

    @HostListener('window:scroll', [])
    onWindowScroll() {
        const navbar = document.getElementById('navbarBlur');

        if (!navbar) {
            return;
        }

        const scrollY = window.scrollY;

        if (scrollY > navbar.offsetHeight) {
            navbar.classList.add('navbar-scrolled');
            navbar.classList.remove('navbar-transparent');
        } else {
            navbar.classList.add('navbar-transparent');
            navbar.classList.remove('navbar-scrolled');
        }
    }
}